import { useEffect, useState } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight, ClipboardCheck, Timer, Trophy } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { loadFreeTestsPage } from '../lib/library';
import type { FreeTest } from '../lib/library';
import { Badge, Button, EmptyState, LoadingState, SectionHeading } from '../components/ui';

const PAGE_SIZE = 15;

export function StudentFreeTests() {
  const navigate = useNavigate();
  const [state, setState] = useState<{ page: number; items: FreeTest[]; total: number; failed: boolean }>({ page: -1, items: [], total: 0, failed: false });
  const [page, setPage] = useState(0);

  useEffect(() => {
    let active = true;
    void loadFreeTestsPage(page, PAGE_SIZE)
      .then(({ items, total }) => { if (active) setState({ page, items, total, failed: false }); })
      .catch(() => { if (active) setState((prev) => ({ ...prev, page, items: [], total: prev.total, failed: true })); });
    return () => { active = false; };
  }, [page]);

  const loading = state.page !== page;
  const tests = state.failed ? [] : state.items;
  const total = state.failed ? 0 : state.total;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  return <div className="tests-page">
    <section className="page-title-row"><div><h1>Free tests</h1><p>Published mock tests open to every learner</p></div></section>
    <section className="test-list-section">
      <SectionHeading title={total ? `Available tests · ${total}` : 'Available tests'} />
      {loading ? <LoadingState /> : tests.length
        ? <div className="test-list">{tests.map((test) => <article className="test-card" key={test.objectId}>
          <div className="test-icon"><ClipboardCheck size={23} /></div>
          <div className="test-copy">
            <div><Badge tone="green">Free</Badge><span className="test-course">{test.CourseTitle ?? 'AyurGuide tests'}</span></div>
            <h3>{test.TestTitle}</h3>
            <div className="test-meta"><span><ClipboardCheck size={14} /> {Number(test.NoOfQuestions ?? 0) || '\u2014'} questions</span><span><Timer size={14} /> {Number(test.Time ?? 60)} min</span><span><Trophy size={14} /> {test.TotalMarks ?? '\u2014'} marks</span></div>
          </div>
          <Button variant="soft" icon={ArrowRight} onClick={() => navigate(`/tests/${test.objectId}`)}>Start</Button>
        </article>)}</div>
        : <EmptyState title="No free tests yet" description="Free mock tests appear here once faculty publishes them." action={<Button variant="soft" onClick={() => navigate('/tests')}>Browse all tests</Button>} />}
      {total > PAGE_SIZE && <div className="pagination-row">
        <Button variant="ghost" icon={ChevronLeft} disabled={page === 0 || loading} onClick={() => setPage((value) => Math.max(0, value - 1))}>Previous</Button>
        <span className="pagination-label">Page {page + 1} of {pageCount}</span>
        <Button variant="ghost" disabled={page + 1 >= pageCount || loading} onClick={() => setPage((value) => value + 1)}>Next <ChevronRight size={16} /></Button>
      </div>}
    </section>
  </div>;
}
